/**
 * Robustness report on every backtest (BE `overview.stability` + `overview.multiple_testing`):
 * year-by-year, top-1% removal, losing streaks, Monte Carlo (as tested / half edge), and how
 * many variants were tried on this data.
 */

export type StrategyRdYearRow = {
  year: number;
  trades: number;
  expectancyR: number | null;
  returnPct: number | null;
};

export type StrategyRdMcCase = {
  dropOver30: number | null;
  dropOver50: number | null;
  medianMaxDropPct: number | null;
  return12m: { p5: number | null; p25: number | null; p50: number | null; p75: number | null; p95: number | null };
  losingYearShare: number | null;
};

export type StrategyRdReport = {
  years: { rows: StrategyRdYearRow[]; positive: number; total: number; headline: string | null } | null;
  outliers: { verdict: string; dropped: number | null; expectancyR: number | null; expectancyWithoutTop: number | null } | null;
  streaks: {
    losingTrades: number | null;
    redDays: number | null;
    redDayShare: number | null;
    worstDay: { date: string; pnl: number | null; pct: number | null } | null;
  } | null;
  monteCarlo: {
    tradesPerYear: number | null;
    /** What the simulated years are built from (older reports: null). */
    trades: number | null;
    spanDays: number | null;
    sims: number | null;
    asTested: StrategyRdMcCase | null;
    halfEdge: StrategyRdMcCase | null;
    note: string | null;
  } | null;
  multipleTesting: { tries: number; verdict: string; message: string | null } | null;
};

const rec = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

function mcCase(raw: unknown): StrategyRdMcCase | null {
  const r = rec(raw);
  if (!r) return null;
  const q = rec(r.return_12m_pct) ?? {};
  return {
    dropOver30: num(r.drop_over_30_pct),
    dropOver50: num(r.drop_over_50_pct),
    medianMaxDropPct: num(r.median_max_drop_pct),
    return12m: { p5: num(q.p5), p25: num(q.p25), p50: num(q.p50), p75: num(q.p75), p95: num(q.p95) },
    losingYearShare: num(r.losing_year_share),
  };
}

/** From the raw `stability` object and the overview's `multiple_testing`. Null when nothing is there. */
export function parseStrategyRdReport(stabilityRaw: unknown, multipleTestingRaw?: unknown): StrategyRdReport | null {
  const st = rec(stabilityRaw) ?? {};
  const y = rec(st.years);
  const o = rec(st.outliers);
  const s = rec(st.streaks);
  const m = rec(st.monte_carlo);
  const mt = rec(multipleTestingRaw);
  const report: StrategyRdReport = {
    years: y
      ? {
          rows: (Array.isArray(y.years) ? y.years : [])
            .map((row) => {
              const r = rec(row);
              const year = num(r?.year);
              return year == null
                ? null
                : { year, trades: num(r?.trades) ?? 0, expectancyR: num(r?.expectancy_r), returnPct: num(r?.return_pct) };
            })
            .filter((r): r is StrategyRdYearRow => r != null),
          positive: num(y.years_positive) ?? 0,
          total: num(y.years_total) ?? 0,
          headline: str(y.headline),
        }
      : null,
    outliers:
      o && str(o.verdict) && o.verdict !== "too_few_trades"
        ? {
            verdict: String(o.verdict),
            dropped: num(o.dropped),
            expectancyR: num(o.expectancy_r),
            expectancyWithoutTop: num(o.expectancy_r_without_top),
          }
        : null,
    streaks: s
      ? {
          losingTrades: num(s.longest_losing_trades),
          redDays: num(s.longest_red_days),
          redDayShare: num(s.red_day_share),
          worstDay: rec(s.worst_day)
            ? { date: String(rec(s.worst_day)?.date ?? ""), pnl: num(rec(s.worst_day)?.pnl), pct: num(rec(s.worst_day)?.pct) }
            : null,
        }
      : null,
    monteCarlo: m
      ? {
          tradesPerYear: num(m.trades_per_year),
          trades: num(m.trades),
          spanDays: num(m.span_days),
          sims: num(m.sims),
          asTested: mcCase(m.as_tested),
          halfEdge: mcCase(m.half_edge),
          note: str(m.note),
        }
      : null,
    multipleTesting:
      mt && num(mt.tries) != null
        ? { tries: num(mt.tries) as number, verdict: String(mt.verdict ?? "few"), message: str(mt.message) }
        : null,
  };
  const empty = !report.years && !report.outliers && !report.streaks && !report.monteCarlo && !report.multipleTesting;
  return empty ? null : report;
}

/** "1 in 8" for a probability (≥ 1 in 1000), "< 1 in 1000" below that, "—" for none. */
export function strategyRdOdds(p: number | null | undefined): string {
  if (p == null) return "N/A";
  if (p <= 0) return "< 1 in 1000";
  if (p >= 1) return "every time";
  return `1 in ${Math.max(1, Math.round(1 / p))}`;
}
